import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Copy, RefreshCw, ExternalLink, FileSignature, CheckCircle2, Clock, XCircle, Download, Trash2, AlertTriangle, Send, FileText, Archive, ArchiveRestore } from "lucide-react";
import { toast } from "sonner";

const STATUS_BADGE = {
  pending: { cls: "bg-amber-50 text-amber-800 border-amber-200", icon: Clock, label: "In attesa" },
  signed: { cls: "bg-emerald-50 text-emerald-800 border-emerald-200", icon: CheckCircle2, label: "Firmato" },
  expired: { cls: "bg-slate-50 text-slate-600 border-slate-200", icon: XCircle, label: "Scaduto" },
  cancelled: { cls: "bg-rose-50 text-rose-700 border-rose-200", icon: XCircle, label: "Annullato" },
};

const DOC_TYPE_LABEL = { quote: "Preventivo", quote_offer: "Offerta", policy: "Polizza" };

export default function Signatures() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  // 🆕 Feb 2026 — Tab archivio
  const [viewArchived, setViewArchived] = useState(false);
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [tabCounts, setTabCounts] = useState({ active: 0, archived: 0 });
  const loadCounts = useCallback(async () => {
    try {
      const { data } = await api.get("/signatures/counts");
      setTabCounts(data || { active: 0, archived: 0 });
    } catch { /* silenzioso */ }
  }, []);  // Selezione multipla per eliminazione bulk (solo admin)
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleteDialog, setDeleteDialog] = useState(null); // { ids, stage: 'request'|'verify', otp, busy }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/signatures", { params: { archived: viewArchived } });
      setItems(data || []);
      setSelectedIds(new Set());
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
    loadCounts();
  }, [viewArchived, loadCounts]);

  useEffect(() => { load(); }, [load]);

  // 🆕 Feb 2026 — Archiviazione / Ripristino bulk
  const archiveSelected = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Spostare ${selectedIds.size} firm${selectedIds.size === 1 ? "a" : "e"} in archivio?\n\nPotrai ripristinarle o eliminarle definitivamente dalla vista Archivio.`)) return;
    setArchiveBusy(true);
    try {
      const { data } = await api.post("/signatures/archive-bulk", { ids: Array.from(selectedIds) });
      toast.success(`${data?.archived || 0} firm${(data?.archived || 0) === 1 ? "a archiviata" : "e archiviate"}`);
      setSelectedIds(new Set());
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setArchiveBusy(false); }
  };
  const unarchiveSelected = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Ripristinare ${selectedIds.size} firm${selectedIds.size === 1 ? "a" : "e"} dall'archivio?`)) return;
    setArchiveBusy(true);
    try {
      const { data } = await api.post("/signatures/unarchive-bulk", { ids: Array.from(selectedIds) });
      toast.success(`${data?.unarchived || 0} firm${(data?.unarchived || 0) === 1 ? "a ripristinata" : "e ripristinate"}`);
      setSelectedIds(new Set());
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setArchiveBusy(false); }
  };

  const copyLink = async (id) => {
    const url = `${window.location.origin}/sign/${id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link firma copiato");
    } catch { toast.error("Impossibile copiare il link"); }
  };

  // 🆕 09/07/2026 — Reinvia OTP al cliente (solo per firme in stato pending)
  const [resending, setResending] = useState(null);
  const resendOtp = async (s) => {
    if (s.status !== "pending") { toast.error("Reinvio possibile solo per firme in attesa"); return; }
    if (!window.confirm(`Reinviare il codice OTP a ${s.client_email || s.client_name || "il cliente"}?`)) return;
    setResending(s.id);
    try {
      const { data } = await api.post(`/signatures/${s.id}/resend-otp`, { channel: "email" });
      if (data?.ok) toast.success(data.delivery_note || "OTP reinviato al cliente");
      else toast.warning(data?.delivery_note || "Reinvio non recapitato");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setResending(null); }
  };

  const filtered = items.filter((s) => {
    if (filter !== "all" && s.status !== filter) return false;
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      (s.client_name || "").toLowerCase().includes(q)
      || (s.client_email || "").toLowerCase().includes(q)
      || (s.id || "").toLowerCase().includes(q)
    );
  });

  // Selezione
  const toggleSelect = (id) => setSelectedIds((prev) => {
    const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n;
  });
  const allFilteredSelected = filtered.length > 0 && filtered.every((s) => selectedIds.has(s.id));
  const toggleSelectAll = () => setSelectedIds(allFilteredSelected ? new Set() : new Set(filtered.map((s) => s.id)));

  // Eliminazione con OTP admin
  const openDelete = (ids) => {
    if (!ids.length) return;
    setDeleteDialog({ ids, stage: "request", otp: "", busy: false });
  };
  const requestOtp = async () => {
    setDeleteDialog((d) => ({ ...d, busy: true }));
    try {
      const { data } = await api.post("/admin/request-action-otp", { action: "delete_signatures" });
      toast.success(`OTP inviato a ${data.delivered_to || "tua email"}`);
      setDeleteDialog((d) => ({ ...d, stage: "verify", busy: false }));
    } catch (e) {
      toast.error(formatApiError(e));
      setDeleteDialog((d) => ({ ...d, busy: false }));
    }
  };
  const confirmDelete = async () => {
    if (!deleteDialog?.otp || deleteDialog.otp.length !== 6) { toast.error("Inserisci OTP a 6 cifre"); return; }
    setDeleteDialog((d) => ({ ...d, busy: true }));
    try {
      const { data } = await api.delete("/signatures", { data: { ids: deleteDialog.ids, otp: deleteDialog.otp } });
      toast.success(`${data.deleted} firm${data.deleted === 1 ? "a eliminata" : "e eliminate"}`);
      setSelectedIds(new Set());
      setDeleteDialog(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
      setDeleteDialog((d) => ({ ...d, busy: false }));
    }
  };

  const counts = items.reduce((acc, s) => { acc[s.status] = (acc[s.status] || 0) + 1; return acc; }, {});

  return (
    <div className="space-y-6 animate-fade-up" data-testid="signatures-page">
      <header className="flex flex-wrap items-center gap-3 justify-between">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold flex items-center gap-2">
            <FileSignature className="h-6 w-6 text-blue-600" /> Firme richieste
          </h1>
          <p className="text-xs text-slate-500 mt-1">Monitora le firme OTP inviate ai clienti per offerte, preventivi e polizze.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="refresh-signatures-btn">
          <RefreshCw className="h-4 w-4 mr-1.5" /> Aggiorna
        </Button>
      </header>

      {/* Filtri */}
      <div className="flex flex-wrap items-center gap-2">
        {["all", "pending", "signed", "expired", "cancelled"].map((k) => {
          const cfg = STATUS_BADGE[k];
          const c = k === "all" ? items.length : counts[k] || 0;
          return (
            <button
              key={k}
              data-testid={`filter-${k}`}
              onClick={() => setFilter(k)}
              className={`text-xs px-3 py-1.5 rounded-full border transition ${filter === k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}
            >
              {k === "all" ? "Tutte" : cfg.label} · <strong>{c}</strong>
            </button>
          );
        })}
        <Input
          placeholder="🔎 Cerca cliente o ID firma…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-8 max-w-[260px] text-sm ml-auto"
          data-testid="signatures-search"
        />
        {/* 🆕 Feb 2026 — Tab Attive / Archivio */}
        <div className="inline-flex items-stretch rounded-md border border-slate-200 bg-white overflow-hidden text-xs" data-testid="sig-view-tabs">
          <button
            type="button"
            onClick={() => setViewArchived(false)}
            className={`px-3 py-1.5 uppercase tracking-wide font-medium transition-colors flex items-center gap-1.5 ${!viewArchived ? "bg-slate-800 text-white" : "text-slate-700 hover:bg-slate-50"}`}
            data-testid="sig-tab-active"
          >
            📄 Attive
            <span className={`text-[10px] font-bold rounded-full px-1.5 py-0.5 leading-none ${!viewArchived ? "bg-white text-slate-800" : "bg-slate-200 text-slate-700"}`}>{tabCounts.active}</span>
          </button>
          <button
            type="button"
            onClick={() => setViewArchived(true)}
            className={`px-3 py-1.5 uppercase tracking-wide font-medium border-l border-slate-200 transition-colors flex items-center gap-1.5 ${viewArchived ? "bg-slate-600 text-white" : "text-slate-700 hover:bg-slate-50"}`}
            data-testid="sig-tab-archive"
          >
            📦 Archivio
            <span className={`text-[10px] font-bold rounded-full px-1.5 py-0.5 leading-none ${viewArchived ? "bg-white text-slate-800" : tabCounts.archived > 0 ? "bg-amber-200 text-amber-900" : "bg-slate-200 text-slate-700"}`}>{tabCounts.archived}</span>
          </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {/* Barra bulk admin */}
        {isAdmin && selectedIds.size > 0 && (
          <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-rose-50 border-b border-rose-200 text-sm" data-testid="sig-bulk-bar">
            <div className="text-rose-800">
              <strong>{selectedIds.size}</strong> firm{selectedIds.size === 1 ? "a selezionata" : "e selezionate"}
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => setSelectedIds(new Set())} data-testid="sig-bulk-clear">Annulla</Button>
              {viewArchived ? (
                <Button
                  size="sm"
                  onClick={unarchiveSelected}
                  disabled={archiveBusy}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="sig-bulk-unarchive"
                >
                  <ArchiveRestore className="h-4 w-4 mr-1.5" /> Ripristina
                </Button>
              ) : (
                <Button
                  size="sm"
                  onClick={archiveSelected}
                  disabled={archiveBusy}
                  className="bg-slate-600 hover:bg-slate-700 text-white"
                  data-testid="sig-bulk-archive"
                >
                  <Archive className="h-4 w-4 mr-1.5" /> Archivia
                </Button>
              )}
              <Button
                size="sm"
                onClick={() => openDelete(Array.from(selectedIds))}
                className="bg-rose-600 hover:bg-rose-700 text-white"
                data-testid="sig-bulk-delete"
              >
                <Trash2 className="h-4 w-4 mr-1.5" /> Elimina con OTP
              </Button>
            </div>
          </div>
        )}
        <table className="w-full text-sm" data-testid="signatures-table">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
            <tr>
              {isAdmin && (
                <th className="px-3 py-3 text-left w-10">
                  <input
                    type="checkbox"
                    checked={allFilteredSelected}
                    onChange={toggleSelectAll}
                    disabled={filtered.length === 0}
                    className="h-4 w-4 cursor-pointer accent-rose-600"
                    data-testid="sig-select-all"
                    title={allFilteredSelected ? "Deseleziona tutto" : "Seleziona tutto (filtrate)"}
                  />
                </th>
              )}
              <th className="px-4 py-3 text-left">Cliente</th>
              <th className="px-4 py-3 text-left">Preventivo / Documento</th>
              <th className="px-4 py-3 text-left">Operatore</th>
              <th className="px-4 py-3 text-left">Stato</th>
              <th className="px-4 py-3 text-left">Data invio OTP</th>
              <th className="px-4 py-3 text-left">Firmata</th>
              <th className="px-4 py-3 text-right">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={isAdmin ? 8 : 7} className="px-4 py-8 text-center text-slate-400">Caricamento…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={isAdmin ? 8 : 7} className="px-4 py-8 text-center text-slate-400">
                {items.length === 0 ? "Nessuna firma richiesta ancora." : "Nessuna firma corrisponde ai filtri."}
              </td></tr>
            ) : filtered.map((s) => {
              const st = STATUS_BADGE[s.status] || STATUS_BADGE.pending;
              const StIcon = st.icon;
              return (
                <tr key={s.id} className={`border-t border-slate-100 hover:bg-slate-50 ${selectedIds.has(s.id) ? "bg-rose-50/50" : ""}`} data-testid={`sig-row-${s.id}`}>
                  {isAdmin && (
                    <td className="px-3 py-3">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(s.id)}
                        onChange={() => toggleSelect(s.id)}
                        className="h-4 w-4 cursor-pointer accent-rose-600"
                        data-testid={`sig-select-${s.id}`}
                      />
                    </td>
                  )}
                  <td className="px-4 py-3">
                    <div className="font-medium">{s.client_name || "—"}</div>
                    <div className="text-xs text-slate-500">{s.client_email || s.client_mobile || "—"}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide bg-slate-100 text-slate-700 border border-slate-300 px-2 py-0.5 rounded max-w-[240px] truncate" title={s.doc_type === "freehand" ? (s.document_title || "Freehand") : (DOC_TYPE_LABEL[s.doc_type] || s.doc_type)}>
                      {s.doc_type === "freehand" ? (s.document_title || DOC_TYPE_LABEL[s.doc_type] || "Freehand") : (DOC_TYPE_LABEL[s.doc_type] || s.doc_type)}
                    </span>
                    {s.doc_number ? (
                      <div className="mt-1 flex items-center gap-1.5">
                        {s.doc_type === "quote_offer" && s.doc_id ? (
                          <a
                            href={`/quotes/offers?focus=${s.doc_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs font-mono font-semibold text-blue-700 hover:underline inline-flex items-center gap-1"
                            title="Apri l'offerta collegata"
                            data-testid={`open-linked-offer-${s.id}`}
                          >
                            <FileText className="h-3 w-3" /> #{s.doc_number}
                          </a>
                        ) : s.doc_type === "policy" && s.doc_id ? (
                          <a
                            href={`/policies?focus=${s.doc_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs font-mono font-semibold text-blue-700 hover:underline inline-flex items-center gap-1"
                            title="Apri la polizza collegata"
                            data-testid={`open-linked-policy-${s.id}`}
                          >
                            <FileText className="h-3 w-3" /> #{s.doc_number}
                          </a>
                        ) : s.linked_quote_id ? (
                          <a
                            href={`/quotes?open=${s.linked_quote_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs font-mono font-semibold text-blue-700 hover:underline inline-flex items-center gap-1"
                            title="Apri il preventivo collegato"
                            data-testid={`open-linked-quote-${s.id}`}
                          >
                            <FileText className="h-3 w-3" /> #{s.doc_number}
                          </a>
                        ) : (
                          <span className="text-xs font-mono font-semibold text-slate-800">#{s.doc_number}</span>
                        )}
                        {s.doc_amount != null && (
                          <span className="text-[10px] text-slate-500 tabular-nums">€ {Number(s.doc_amount).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        )}
                      </div>
                    ) : (
                      <div className="text-[10px] text-slate-400 font-mono mt-0.5">{(s.doc_id || "").slice(0, 8)}…</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs" data-testid={`sig-operator-${s.id}`}>
                    {s.created_by_name ? (
                      <div className="font-medium text-slate-800">{s.created_by_name}</div>
                    ) : (
                      <span className="text-slate-400 italic">—</span>
                    )}
                    {s.created_by_email && s.created_by_name !== s.created_by_email && (
                      <div className="text-[10px] text-slate-500">{s.created_by_email}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide border rounded-full px-2 py-0.5 ${st.cls}`}>
                      <StIcon className="h-3 w-3" /> {st.label}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">
                    {s.otp_sent_at ? new Date(s.otp_sent_at).toLocaleString("it-IT") : (s.created_at ? new Date(s.created_at).toLocaleString("it-IT") : "—")}
                    {s.otp_channel_used && (
                      <div className="text-[10px] text-slate-400 mt-0.5">via {s.otp_channel_used}</div>
                    )}
                    {s.resent_at && (
                      <div className="text-[10px] text-indigo-500 mt-0.5" title={`Reinviato il ${new Date(s.resent_at).toLocaleString("it-IT")}`}>↻ reinviato</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">
                    {s.signed_at ? new Date(s.signed_at).toLocaleString("it-IT") : "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {s.status === "pending" && (
                        <button
                          data-testid={`resend-otp-${s.id}`}
                          onClick={() => resendOtp(s)}
                          disabled={resending === s.id}
                          className="p-1.5 rounded hover:bg-indigo-50 text-indigo-600 disabled:opacity-50"
                          title="Reinvia OTP al cliente via email"
                        >
                          {resending === s.id ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        </button>
                      )}
                      {s.status === "signed" && (s.doc_type === "quote_offer" || s.doc_type === "freehand") && (
                        <button
                          data-testid={`download-signed-pdf-${s.id}`}
                          onClick={async () => {
                            try {
                              const res = await api.get(`/signatures/${s.id}/signed-pdf`, { responseType: "blob" });
                              const blob = new Blob([res.data], { type: "application/pdf" });
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement("a");
                              const prefix = s.doc_type === "freehand" ? "Documento" : "Offerta";
                              const title = (s.document_title || "").replace(/[\\/"]+/g, "_").trim();
                              a.href = url;
                              a.download = title ? `${title}_firmato.pdf` : `${prefix}_firmato_${s.id.slice(0,8)}.pdf`;
                              document.body.appendChild(a); a.click(); document.body.removeChild(a);
                              URL.revokeObjectURL(url);
                            } catch (e) { toast.error(formatApiError(e)); }
                          }}
                          className="p-1.5 rounded hover:bg-emerald-50 text-emerald-700"
                          title="Scarica PDF firmato con audit trail"
                        >
                          <Download className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        data-testid={`copy-sign-link-${s.id}`}
                        onClick={() => copyLink(s.id)}
                        className="p-1.5 rounded hover:bg-blue-50 text-blue-600"
                        title="Copia link firma"
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                      <a
                        data-testid={`open-sign-${s.id}`}
                        href={`/sign/${s.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1.5 rounded hover:bg-slate-100 text-slate-700"
                        title="Apri pagina firma"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                      {isAdmin && (
                        <>
                          {viewArchived ? (
                            <button
                              data-testid={`unarchive-sig-${s.id}`}
                              onClick={async () => {
                                if (!window.confirm("Ripristinare questa firma?")) return;
                                setArchiveBusy(true);
                                try {
                                  await api.post("/signatures/unarchive-bulk", { ids: [s.id] });
                                  toast.success("Firma ripristinata");
                                  load();
                                } catch (e) { toast.error(formatApiError(e)); }
                                finally { setArchiveBusy(false); }
                              }}
                              className="p-1.5 rounded hover:bg-emerald-50 text-emerald-700"
                              title="Ripristina dall'archivio"
                              disabled={archiveBusy}
                            >
                              <ArchiveRestore className="h-4 w-4" />
                            </button>
                          ) : (
                            <button
                              data-testid={`archive-sig-${s.id}`}
                              onClick={async () => {
                                if (!window.confirm("Spostare questa firma in archivio?")) return;
                                setArchiveBusy(true);
                                try {
                                  await api.post("/signatures/archive-bulk", { ids: [s.id] });
                                  toast.success("Firma archiviata");
                                  load();
                                } catch (e) { toast.error(formatApiError(e)); }
                                finally { setArchiveBusy(false); }
                              }}
                              className="p-1.5 rounded hover:bg-slate-100 text-slate-700"
                              title="Sposta in archivio"
                              disabled={archiveBusy}
                            >
                              <Archive className="h-4 w-4" />
                            </button>
                          )}
                          <button
                            data-testid={`delete-sig-${s.id}`}
                            onClick={() => openDelete([s.id])}
                            className="p-1.5 rounded hover:bg-rose-50 text-rose-600"
                            title="Elimina firma (richiede OTP admin)"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Dialog conferma eliminazione con OTP admin */}
      <Dialog open={!!deleteDialog} onOpenChange={(o) => { if (!o && !deleteDialog?.busy) setDeleteDialog(null); }}>
        <DialogContent className="w-[95vw] max-w-md" data-testid="sig-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-rose-600" />
              Elimina {deleteDialog?.ids.length === 1 ? "firma" : `${deleteDialog?.ids.length || 0} firme`}
            </DialogTitle>
          </DialogHeader>
          {deleteDialog?.stage === "request" ? (
            <div className="space-y-3 text-sm">
              <p className="text-slate-700">
                Per motivi di sicurezza è richiesta una <strong>conferma OTP</strong> via email amministratore prima di eliminare {deleteDialog?.ids.length === 1 ? "questa firma" : "queste firme"}.
              </p>
              <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900">
                ⚠️ L&apos;eliminazione è <strong>definitiva</strong> e cancella anche l&apos;audit trail della firma.
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-slate-700">Inserisci il codice OTP a 6 cifre ricevuto via email:</p>
              <Input
                type="text"
                inputMode="numeric"
                maxLength={6}
                autoFocus
                value={deleteDialog?.otp || ""}
                onChange={(e) => setDeleteDialog({ ...deleteDialog, otp: e.target.value.replace(/\D/g, "") })}
                placeholder="••••••"
                className="text-center text-2xl font-mono tracking-[0.4em]"
                data-testid="sig-delete-otp-input"
              />
              <p className="text-[11px] text-slate-500">Validità OTP: 10 minuti · Max 5 tentativi.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(null)} disabled={deleteDialog?.busy}>Annulla</Button>
            {deleteDialog?.stage === "request" ? (
              <Button onClick={requestOtp} disabled={deleteDialog?.busy} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="sig-delete-request-otp">
                {deleteDialog?.busy ? "Invio…" : "📧 Invia OTP via email"}
              </Button>
            ) : (
              <Button onClick={confirmDelete} disabled={deleteDialog?.busy || (deleteDialog?.otp || "").length !== 6} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="sig-delete-confirm">
                <Trash2 className="h-4 w-4 mr-1.5" /> {deleteDialog?.busy ? "Elimino…" : "Conferma eliminazione"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
